import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosInstance';
import apiConfig from '../api/apiConfig';

const EnhancedCorsTest = () => {
  const [testResults, setTestResults] = useState({
    ping: null,
    corsTest: null,
    environment: null
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    showHeaders: false,
    useAxios: true,
    withCredentials: axiosInstance.defaults.withCredentials
  });

  // Get environment info
  useEffect(() => {
    setTestResults(prev => ({
      ...prev,
      environment: {
        baseUrl: apiConfig.baseURL,
        nodeEnv: process.env.NODE_ENV,
        origin: window.location.origin,
        withCredentials: axiosInstance.defaults.withCredentials
      }
    }));
  }, []);

  const runTests = async () => {
    setLoading(true);
    setErrors({});
    
    const makeRequest = (url, method = 'GET') => {
      if (settings.useAxios) {
        return axiosInstance({
          url,
          method,
          withCredentials: settings.withCredentials
        });
      } else {
        // Use native fetch
        return fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: settings.withCredentials ? 'include' : 'same-origin'
        }).then(res => {
          if (!res.ok) throw new Error(`HTTP error ${res.status}`);
          return res.json();
        });
      }
    };

    // Test ping endpoint
    try {
      console.log('Testing ping endpoint...');
      const pingUrl = `${apiConfig.baseURL}/ping`;
      
      const pingResponse = await makeRequest(pingUrl);
      console.log('Ping response:', pingResponse);
      
      setTestResults(prev => ({
        ...prev,
        ping: {
          success: true,
          data: pingResponse.data || pingResponse,
          headers: settings.useAxios ? pingResponse.headers : undefined
        }
      }));
    } catch (error) {
      console.error('Ping test failed:', error);
      setErrors(prev => ({ 
        ...prev, 
        ping: {
          message: error.message,
          details: error.response?.data || error.toString()
        }
      }));
    }

    // Test CORS endpoint
    try {
      console.log('Testing CORS endpoint...');
      const corsUrl = `${apiConfig.baseURL}/cors-test`;
      
      const corsResponse = await makeRequest(corsUrl);
      console.log('CORS test response:', corsResponse);
      
      setTestResults(prev => ({
        ...prev,
        corsTest: {
          success: true,
          data: corsResponse.data || corsResponse,
          headers: settings.useAxios ? corsResponse.headers : undefined
        }
      }));
    } catch (error) {
      console.error('CORS test failed:', error);
      setErrors(prev => ({ 
        ...prev, 
        corsTest: {
          message: error.message,
          details: error.response?.data || error.toString()
        }
      }));
    }

    setLoading(false);
  };

  // Toggle settings
  const toggleSetting = (setting) => {
    setSettings(prev => ({ ...prev, [setting]: !prev[setting] }));
  };
  
  // Format JSON for display
  const formatJson = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  };
  
  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif',
      maxWidth: '800px', 
      margin: '0 auto', 
      padding: '20px',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      <h1 style={{ color: '#333', borderBottom: '1px solid #ddd', paddingBottom: '10px' }}>CORS Configuration Test</h1>
      
      <div style={{ marginBottom: '20px', backgroundColor: '#e9f7ef', padding: '15px', borderRadius: '5px' }}>
        <h2>Environment Information</h2>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '5px' }}>
          <strong>Backend URL:</strong> <span>{apiConfig.baseURL}</span>
          <strong>Environment:</strong> <span>{process.env.NODE_ENV}</span>
          <strong>Frontend Origin:</strong> <span>{window.location.origin}</span>
          <strong>With Credentials:</strong> <span>{String(axiosInstance.defaults.withCredentials)}</span>
        </div>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <h2>Test Settings</h2>
        <div style={{ display: 'flex', gap: '15px' }}>
          <label>
            <input 
              type="checkbox" 
              checked={settings.useAxios}
              onChange={() => toggleSetting('useAxios')}
            />
            Use Axios (uncheck for Fetch API)
          </label>
          
          <label>
            <input 
              type="checkbox" 
              checked={settings.withCredentials}
              onChange={() => toggleSetting('withCredentials')}
            />
            Send with credentials
          </label>
          
          <label>
            <input 
              type="checkbox" 
              checked={settings.showHeaders}
              onChange={() => toggleSetting('showHeaders')}
            />
            Show response headers
          </label>
        </div>
      </div>
      
      <button 
        onClick={runTests}
        disabled={loading}
        style={{
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '10px 15px',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          marginBottom: '20px'
        }}
      >
        {loading ? 'Testing...' : 'Run CORS Tests'}
      </button>
      
      {/* Ping Test Results */}
      <div style={{ marginBottom: '20px' }}>
        <h2>Ping Test</h2>
        {loading && <p>Testing ping endpoint...</p>}
        
        {errors.ping && (
          <div style={{ color: '#D32F2F', backgroundColor: '#FFEBEE', padding: '10px', borderRadius: '5px' }}>
            <h3>Error</h3>
            <p>{errors.ping.message}</p>
            {errors.ping.details && <pre>{errors.ping.details}</pre>}
          </div>
        )}
        
        {testResults.ping && testResults.ping.success && (
          <div style={{ color: '#388E3C', backgroundColor: '#E8F5E9', padding: '10px', borderRadius: '5px' }}>
            <h3>Success</h3>
            <pre>{formatJson(testResults.ping.data)}</pre>
            
            {settings.showHeaders && testResults.ping.headers && (
              <>
                <h4>Headers</h4>
                <pre>{formatJson(testResults.ping.headers)}</pre>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* CORS Test Results */}
      <div style={{ marginBottom: '20px' }}>
        <h2>CORS Test</h2>
        {loading && <p>Testing CORS endpoint...</p>}
        
        {errors.corsTest && (
          <div style={{ color: '#D32F2F', backgroundColor: '#FFEBEE', padding: '10px', borderRadius: '5px' }}>
            <h3>Error</h3>
            <p>{errors.corsTest.message}</p>
            {errors.corsTest.details && <pre>{errors.corsTest.details}</pre>}
          </div>
        )}
        
        {testResults.corsTest && testResults.corsTest.success && (
          <div style={{ color: '#388E3C', backgroundColor: '#E8F5E9', padding: '10px', borderRadius: '5px' }}>
            <h3>Success</h3>
            <pre>{formatJson(testResults.corsTest.data)}</pre>
            
            {settings.showHeaders && testResults.corsTest.headers && (
              <>
                <h4>Headers</h4>
                <pre>{formatJson(testResults.corsTest.headers)}</pre>
              </>
            )}
          </div>
        )}
      </div>
      
      <div style={{ fontSize: '0.9em', borderTop: '1px solid #ddd', paddingTop: '10px', color: '#666' }}>
        <p>
          <strong>Troubleshooting Tips:</strong>
          <ul>
            <li>Check browser console for detailed error messages</li>
            <li>Verify backend CORS configuration in server.js</li>
            <li>Ensure frontend and backend origins match in the CORS configuration</li>
            <li>Try toggling "withCredentials" setting if you're having problems</li>
          </ul>
        </p>
      </div>
    </div>
  );
};

export default EnhancedCorsTest;
